/**
 * The page speed card of a Saidiseire site page (item C,
 * docs/saidiseire-kiirus-turve-raport.md): the weekly Google PageSpeed
 * Insights scores, mobile and desktop, with their six lab metrics, the mobile
 * trend and a run on request.
 *
 * Named page speed on purpose, and kept apart from the response time card
 * above it: that one is how fast the server starts answering our uptime
 * checks, this one is how the whole page loads in a browser, as Lighthouse
 * measures it. The package sold "speed monitoring" and the two are easy to
 * confuse.
 *
 * One card, a few stories, told first to last (`speedCardState`): a run going
 * on (the card asks every five seconds, and after `SPEED_POLL_LIMIT_MS` says
 * the run is still going and stops asking); a package without the test (a
 * site with no package at all is told the test needs one); no PageSpeed key
 * (nothing is measured, and the card points someone who may open it to
 * Settings > Integrations > PageSpeed); never measured; and the results. The newest
 * run's failure is said in red under whichever of these applies, and the
 * older results stay visible under it.
 *
 * A spent PageSpeed call budget is not a failure: while it is used up
 * (`budgetSpent`, whenever the site is next due) the card says so in a calm
 * line and there is no run-now button, since the API would refuse one. The
 * next run's line gives the later of the weekly moment and the moment the
 * budget comes back. Google's own 429 (`quota`) is then told by the calm
 * line, not in red. A 429 for Google's per-minute limit (`rate_limited`)
 * spends no budget, so it is the newest run's failure like any other, with
 * its own sentence.
 *
 * The run-now button answers to `manage:crm.monitoring`; reading is
 * `view:crm` like the rest of the page. A refusal is worded by the section's
 * own reader (`monitorErrorKey`), because the run on request refuses with the
 * codes the other monitoring routes use.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { Gauge, Loader2, Play, RefreshCw } from "lucide-react";
import { monitorErrorKey } from "../../../../lib/monitoring";
import {
  canRunNow,
  lastErrorKey,
  lastMeasuredAt,
  limitKey,
  shownLastError,
  speedCardState,
  speedErrorKey,
  type SpeedCardResponse,
  type SpeedCardState,
  type SpeedLastError,
} from "../../../../lib/monitoringSpeed";
import { Btn, CardHead, StatusPill, fmtDateTime, useToast } from "../../../ui";
import type { T } from "../words";
import { SpeedScores } from "./SpeedScores";
import { SpeedTrend } from "./SpeedTrend";
import { useSpeed } from "./useSpeed";

export interface SpeedCardProps {
  monitorId: string;
  /** The site's package includes the speed test (`MonitorPolicyView.speedTest`). */
  allowed: boolean;
  /** The site has a package at all (`MonitorPolicyView.source === "plan"`). */
  hasPackage: boolean;
  /** `manage:crm.monitoring`: may start a run on request, and may open the PageSpeed settings. */
  canManage: boolean;
  t: T;
}

const STATE_TONE: Readonly<Record<Exclude<SpeedCardState, "measured">, "info" | "mut" | "wt">> = {
  running: "info",
  notInPlan: "mut",
  noKey: "wt",
  never: "mut",
};

function lastErrorText(t: T, e: SpeedLastError): string {
  const code = speedErrorKey(e.error);
  return t(lastErrorKey(e), {
    when: fmtDateTime(e.at),
    // The strategy sits mid-sentence, in brackets, so it is lower case there ("both" has a sentence of its own).
    strategy: e.strategy === "both" ? "" : t(`monitoring.speed.strategy.${e.strategy}`).toLocaleLowerCase(),
    reason: code ? t(`monitoring.speed.error.${code}`) : e.error,
  });
}

/** The line under the results: when the next weekly run is due. Null when none is (the card has said why). */
function nextDueText(t: T, data: SpeedCardResponse): string | null {
  if (!data.nextDueAt || data.running) return null;
  return Date.parse(data.nextDueAt) <= Date.now()
    ? t("monitoring.speed.nextDueSoon")
    : t("monitoring.speed.nextDue", { when: fmtDateTime(data.nextDueAt) });
}

function StateBody({
  data,
  state,
  stillRunning,
  hasPackage,
  canManage,
  onReload,
  t,
}: {
  data: SpeedCardResponse;
  state: SpeedCardState;
  stillRunning: boolean;
  hasPackage: boolean;
  canManage: boolean;
  onReload: () => void;
  t: T;
}) {
  const measured = Boolean(data.latest.mobile || data.latest.desktop);
  if (state === "running") {
    return stillRunning ? (
      <div className="flex flex-wrap items-center gap-3 px-[26px] pb-4">
        <p className="crm-sub text-[13px]">{t("monitoring.speed.stillRunning")}</p>
        <Btn small onClick={onReload}>
          <RefreshCw className="size-[15px]" strokeWidth={1.8} />
          {t("monitoring.speed.refresh")}
        </Btn>
      </div>
    ) : (
      <p className="crm-sub flex items-center gap-2 px-[26px] pb-4 text-[13px]">
        <Loader2 className="size-4 shrink-0 animate-spin" />
        {t("monitoring.speed.runningBody")}
      </p>
    );
  }
  if (state === "notInPlan") {
    return (
      <p className="crm-sub px-[26px] pb-4 text-[13px]">
        {t(hasPackage ? "monitoring.speed.notInPlan" : "monitoring.speed.noPackage")} {measured ? t("monitoring.speed.notInPlanKept") : null}
      </p>
    );
  }
  if (state === "noKey") {
    return (
      <div className="px-[26px] pb-4 text-[13px]">
        <p className="crm-sub">{t("monitoring.speed.noKey")}</p>
        {/* The PageSpeed tab needs `manage:crm.monitoring`: anyone else is told who can add the key. */}
        {canManage ? (
          <Link to="/settings/integrations/pagespeed" className="mt-1 inline-block font-medium text-[var(--color-primary)] hover:underline">
            {t("monitoring.speed.noKeyLink")}
          </Link>
        ) : (
          <p className="crm-sub mt-1">{t("monitoring.speed.noKeyAsk")}</p>
        )}
      </div>
    );
  }
  if (state === "never") return <p className="crm-sub px-[26px] pb-4 text-[13px]">{t("monitoring.speed.never")}</p>;
  return null;
}

export function SpeedCard({ monitorId, allowed, hasPackage, canManage, t }: SpeedCardProps) {
  const { data, failed, stillRunning, reload, runNow } = useSpeed(monitorId, allowed);
  const toast = useToast();
  const [starting, setStarting] = useState(false);

  async function start() {
    setStarting(true);
    try {
      await runNow();
    } catch (err) {
      toast(t(`monitoring.error.${monitorErrorKey(err)}`));
      // A refusal often means the card is behind (a run somebody else started, a package or key changed).
      void reload();
    } finally {
      setStarting(false);
    }
  }

  const title = (
    <span className="inline-flex items-center gap-2">
      <Gauge className="size-4 text-muted-foreground" strokeWidth={1.8} />
      {t("monitoring.speed.title")}
    </span>
  );

  if (!data) {
    return (
      <section className="crm-card mb-4">
        <CardHead title={title} sub={t("monitoring.speed.sub")} />
        <div className="flex justify-center px-[26px] pb-6 text-muted-foreground">
          {failed ? (
            <div className="text-center">
              <p className="mb-3 text-[13px] text-[var(--color-destructive)]">{t("monitoring.speed.loadFailed")}</p>
              <Btn small onClick={() => void reload()}>
                {t("monitoring.retry")}
              </Btn>
            </div>
          ) : (
            <Loader2 className="size-5 animate-spin" />
          )}
        </div>
      </section>
    );
  }

  const state = speedCardState(data);
  const failure = shownLastError(data);
  const measuredAt = lastMeasuredAt(data.latest);
  const measured = measuredAt !== null;
  const next = nextDueText(t, data);

  const actions = (
    <>
      {state !== "measured" ? (
        <StatusPill tone={STATE_TONE[state]}>
          {t(state === "notInPlan" && !hasPackage ? "monitoring.speed.state.noPackage" : `monitoring.speed.state.${state}`)}
        </StatusPill>
      ) : null}
      {canManage && canRunNow(data) ? (
        <Btn small tone="dark" disabled={starting} onClick={() => void start()}>
          {starting ? <Loader2 className="size-[15px] animate-spin" /> : <Play className="size-[15px]" strokeWidth={1.8} />}
          {t("monitoring.speed.runNow")}
        </Btn>
      ) : null}
    </>
  );

  return (
    <section className="crm-card mb-4">
      <CardHead
        title={title}
        sub={measuredAt ? t("monitoring.speed.measuredAt", { when: fmtDateTime(measuredAt) }) : t("monitoring.speed.sub")}
        actions={actions}
      />
      <StateBody
        data={data}
        state={state}
        stillRunning={stillRunning}
        hasPackage={hasPackage}
        canManage={canManage}
        onReload={() => void reload()}
        t={t}
      />
      {data.budgetSpent && state !== "running" ? <p className="crm-sub px-[26px] pb-4 text-[13px]">{t(limitKey(data.budgetSpent))}</p> : null}
      {failure ? <p className="px-[26px] pb-4 text-[13px] text-[var(--color-destructive)]">{lastErrorText(t, failure)}</p> : null}
      {measured ? (
        <>
          <SpeedScores latest={data.latest} t={t} />
          <SpeedTrend history={data.history} t={t} />
        </>
      ) : null}
      {measured || next ? (
        <p className="crm-mut flex flex-wrap gap-x-3 gap-y-1 px-[26px] pb-5 pt-4 text-[12px]">
          {next ? <span>{next}</span> : null}
          {measured ? <span>{t("monitoring.speed.attribution")}</span> : null}
        </p>
      ) : null}
    </section>
  );
}
